import { Product } from "@omni/types/api/product/bff/product.model";
import { ProductFieldSet } from "@omni/types/api/product/bff/shared/enums/productFieldSet.enum";
import queryString from "query-string";
import { useEffect, useState } from "react";

import * as LShapeCalculator from "./calculation/L-shape.calculation";
import * as RectCalculator from "./calculation/rectangular.calculation";

export enum RoofShape {
    RECTANGLE = "RECTANGLE",
    L_SHAPED = "L_SHAPED"
}

export interface RoofShapeProperties {
    widthA1: string | null;
    heightB1: string | null;
    lengthC1: string | null;
    widthW1: string | null;
    lengthC2: string | null;
    widthW2: string | null;
}

export type PropertiesToNumber<T> = {
    [K in keyof T]: number;
};

export type EPDMShoppingList = {
    flexifoilOverlapGlueProduct?: Product<ProductFieldSet.ProductTile>;
    flexifoilSurfaceGlueProduct?: Product<ProductFieldSet.ProductTile>;
    flexifoilSurfaceGlue: number;
    flexifoilBordersGlueProduct?: Product<ProductFieldSet.ProductTile>;
    flexifoilBordersGlue: number;
    epdmInfo: {
        product?: Product<ProductFieldSet.ProductTile> | undefined;
        amount: number;
    }[];
    isolationProduct?: Product<ProductFieldSet.ProductTile>;
    isolationAmount: number;
};

const EPMD_LENGT_FEATURE_CODE =
    "Hubo_Classification_Catalog/GPC/gr-mdm-general-dimensions.att_mdm_general_lengteartikel";

type Props = {
    SURFACE_GLUE_SKU: string;
    BORDER_GLUE_SKU: string;
    EPDM_SKUS: Array<string>;
    OVERLAP_GLUE_SKU: string;
};

export const useEPDMCalculation = ({
    SURFACE_GLUE_SKU,
    BORDER_GLUE_SKU,
    EPDM_SKUS,
    OVERLAP_GLUE_SKU
}: Props) => {
    const [productData, setProductData] = useState<
        Array<Product<ProductFieldSet.ProductTile>>
    >([]);
    const [roofShape, setRoofShape] = useState<RoofShape>(RoofShape.RECTANGLE);
    const [rectangularAmountEPDM, setRectangularAmountEPDM] = useState(0);
    const [isolationRequired, setIsolationRequired] = useState(false);
    const [isolationProduct, setIsolationProduct] = useState<
        undefined | Product<ProductFieldSet.ProductTile>
    >(undefined);
    const [extraAmountEPDM, setExtraAmountEPDM] = useState(0);
    const [roofShapeProperties, setRoofShapeProperties] =
        useState<RoofShapeProperties>({
            widthA1: null,
            heightB1: null,
            lengthC1: null,
            widthW1: null,
            lengthC2: null,
            widthW2: null
        });
    const [shoppingList, setShoppingList] = useState<EPDMShoppingList>({
        flexifoilSurfaceGlue: 0,
        flexifoilBordersGlue: 0,
        epdmInfo: [],
        isolationAmount: 0
    });

    useEffect(() => {
        const q = queryString.parse(location.search);

        setRoofShape((q?.roofShape as RoofShape) || RoofShape.RECTANGLE);
        setRoofShapeProperties({
            widthA1: q.widthA1?.toString() || null,
            heightB1: q.heightB1?.toString() || null,
            lengthC1: q.lengthC1?.toString() || null,
            widthW1: q.widthW1?.toString() || null,
            lengthC2: q.lengthC2?.toString() || null,
            widthW2: q.widthW2?.toString() || null
        });
    }, []);

    const calculateShoppingList = (
        widthA1: number,
        widthW1: number,
        widthW2: number,
        lengthC1: number,
        lengthC2: number,
        heightB1: number
    ) => {
        const flexifoilSurfaceGlue =
            roofShape === RoofShape.RECTANGLE
                ? RectCalculator.flexifoilSurfaceGlue(lengthC1, widthW1)
                : LShapeCalculator.flexifoilSurfaceGlue(
                      lengthC1,
                      widthW1,
                      lengthC2,
                      widthW2
                  );

        const flexifoilBordersGlue =
            roofShape === RoofShape.RECTANGLE
                ? RectCalculator.flexifoilBordersGlue(
                      lengthC1,
                      widthW1,
                      heightB1,
                      widthA1
                  )
                : LShapeCalculator.flexifoilBordersGlue(
                      lengthC1,
                      widthW1,
                      heightB1,
                      widthA1,
                      widthW2
                  );

        const isolationAmount =
            (roofShape === RoofShape.RECTANGLE
                ? RectCalculator.isolationAmount(lengthC1, widthW1)
                : LShapeCalculator.isolationAmount(
                      lengthC1,
                      widthW1,
                      lengthC2,
                      widthW2
                  )) /
            10_000 /
            2.88;

        const surfaceGlueProduct = productData.find(
            (p) => p.sku === SURFACE_GLUE_SKU
        );

        const bordersGlueProduct = productData.find(
            (p) => p.sku === BORDER_GLUE_SKU
        );

        const flexifoilSurfaceGlueProduct =
            flexifoilBordersGlue > 0 && surfaceGlueProduct
                ? surfaceGlueProduct
                : undefined;
        const flexifoilBordersGlueProduct =
            flexifoilBordersGlue > 0 && bordersGlueProduct
                ? bordersGlueProduct
                : undefined;

        const availableDefaultSizes = productData
            .filter((p) => EPDM_SKUS.some((s) => s === p.sku))
            .map((p) => {
                const lengthFeature = p.features.find(
                    (f) => f.code === EPMD_LENGT_FEATURE_CODE
                );

                let size = Number(
                    lengthFeature?.featureValues
                        .at(0)
                        ?.formattedValue?.replace(",", ".")
                );
                if (size && lengthFeature?.unit?.code === "CMT") {
                    size = size / 100;
                }

                return {
                    size,
                    product: p
                };
            });

        const epdmInfo =
            roofShape === RoofShape.RECTANGLE
                ? RectCalculator.epmd(
                      availableDefaultSizes,
                      lengthC1,
                      widthA1,
                      heightB1,
                      widthW1
                  )
                : LShapeCalculator.epmd(
                      availableDefaultSizes,
                      lengthC1,
                      widthA1,
                      lengthC2,
                      heightB1,
                      widthW1,
                      widthW2
                  );

        let flexifoilOverlapGlueProduct;
        if (epdmInfo.length > 1) {
            flexifoilOverlapGlueProduct = productData.find(
                (p) => p.sku === OVERLAP_GLUE_SKU
            );
        }

        setShoppingList({
            flexifoilOverlapGlueProduct,
            flexifoilSurfaceGlueProduct,
            flexifoilSurfaceGlue: flexifoilSurfaceGlue,
            flexifoilBordersGlueProduct,
            flexifoilBordersGlue: flexifoilBordersGlue,
            epdmInfo,
            isolationAmount,
            isolationProduct: isolationRequired ? isolationProduct : undefined
        });
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        const { widthA1, widthW1, widthW2, lengthC1, lengthC2, heightB1 } =
            Object.keys(roofShapeProperties).reduce(
                (acc, key) => ({
                    ...acc,
                    [key]: Number(
                        roofShapeProperties[key as keyof RoofShapeProperties]
                    )
                }),
                {} as PropertiesToNumber<RoofShapeProperties>
            );

        setRectangularAmountEPDM(
            RectCalculator.surfaceArea(widthA1, heightB1, lengthC1, widthW1)
        );
        setExtraAmountEPDM(
            LShapeCalculator.extraSurfaceArea(
                widthA1,
                heightB1,
                lengthC2,
                widthW2
            )
        );

        calculateShoppingList(
            widthA1,
            widthW1,
            widthW2,
            lengthC1,
            lengthC2,
            heightB1
        );
    }, [roofShapeProperties, productData, isolationProduct, roofShape]);

    const hasCalculationValues =
        (roofShape === RoofShape.RECTANGLE
            ? rectangularAmountEPDM
            : rectangularAmountEPDM + extraAmountEPDM) /
            10_000 >
        0.04;

    return {
        setProductData,
        setRoofShape,
        rectangularAmountEPDM,
        extraAmountEPDM,
        roofShapeProperties,
        roofShape,
        setRoofShapeProperties,
        setIsolationProduct,
        isolationProduct: isolationRequired ? isolationProduct : undefined,
        shoppingList,
        isolationRequired,
        setIsolationRequired,
        hasCalculationValues
    };
};
